import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { CapitalizeReceipt, FaReconciliation, PendingReceiptRow } from '@dcc-erp/shared';

import { api, apiJson } from '../api';

export type { CapitalizeReceipt, FaReconciliation, PendingReceiptRow };

// ============================================================
// Local types — mirror packages/shared/src/schemas/fixed-asset.ts.
// Inlined here because the shared barrel index is owned by the Phase-3
// consolidation step; the canonical Zod schema file is committed in
// /packages/shared/src/schemas/fixed-asset.ts and will be wired through
// the barrel by that downstream merge. Until then we keep the type
// contract local to avoid hard import errors.
// ============================================================

export type FixedAssetStatus = 'ACTIVE' | 'DISPOSED' | 'FULLY_DEPRECIATED';
export type DepreciationMethod = 'STRAIGHT_LINE';
export type DepreciationEntryStatus = 'PENDING' | 'POSTED';

export interface FixedAsset {
  id: string;
  companyId: string;
  voucherNo: string;
  code: string;
  name: string;
  categoryCode: string;
  branchId?: string | null;
  departmentId?: string | null;
  acquisitionDate: string;
  costAmount: string | number;
  salvageValue: string | number;
  usefulLifeMonths: number;
  depreciationMethod: DepreciationMethod;
  accumulatedDepreciation: string | number;
  nbv: string | number;
  status: FixedAssetStatus;
  disposedDate?: string | null;
  disposedAmount?: string | number | null;
  journalEntryId?: string | null;
  disposedJournalEntryId?: string | null;
  postedBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FixedAssetListRow extends FixedAsset {
  branchName?: string | null;
  categoryName?: string | null;
}

export interface CreateFixedAsset {
  code: string;
  name: string;
  categoryCode: string;
  branchId?: string | null;
  departmentId?: string | null;
  acquisitionDate: string;
  // ISO date — defaults to acquisitionDate server-side when omitted.
  depreciationStartDate?: string;
  costAmount: number;
  salvageValue?: number;
  usefulLifeMonths: number;
  depreciationMethod?: DepreciationMethod;
  fundingAccountCode?: string;
  expenseAccountCode?: string;
}

export interface DisposeFixedAsset {
  date: string;
  amount: number;
}

export interface DepreciationEntry {
  id: string;
  assetId: string;
  period: string;
  amount: string | number;
  accumulatedAfter: string | number;
  status: DepreciationEntryStatus;
  journalEntryId?: string | null;
  postedAt?: string | null;
  postedBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RunDepreciation {
  period: string;
}

export interface RunDepreciationResponse {
  period: string;
  entryCount: number;
  totalAmount: string;
  journalEntryId: string | null;
  idempotentReplay: boolean;
}

// Cache-key scopes — keyed by companyId so DCC/TTOP/RCI switches give
// independent caches (same pattern as ar/journal-entries).
const assetsKey = (companyId: string | undefined) => ['fixed-assets', companyId] as const;
const depKey = (companyId: string | undefined, assetId: string | undefined) =>
  ['fixed-assets-dep', companyId, assetId] as const;

export interface FixedAssetFilters {
  categoryCode?: string;
  branchId?: string;
  departmentId?: string;
  status?: FixedAssetStatus;
}

export function useFixedAssets(companyId: string | undefined, filters: FixedAssetFilters = {}) {
  const qs = new URLSearchParams();
  if (filters.categoryCode) qs.set('categoryCode', filters.categoryCode);
  if (filters.branchId) qs.set('branchId', filters.branchId);
  if (filters.departmentId) qs.set('departmentId', filters.departmentId);
  if (filters.status) qs.set('status', filters.status);
  const search = qs.toString();
  return useQuery({
    queryKey: [...assetsKey(companyId), filters],
    queryFn: () =>
      api<FixedAssetListRow[]>(`/companies/${companyId}/fixed-assets${search ? `?${search}` : ''}`),
    enabled: !!companyId,
  });
}

export function useFixedAsset(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['fixed-assets', companyId, id],
    queryFn: () => api<FixedAssetListRow>(`/companies/${companyId}/fixed-assets/${id}`),
    enabled: !!companyId && !!id,
  });
}

export function useFixedAssetDepreciation(
  companyId: string | undefined,
  assetId: string | undefined,
) {
  return useQuery({
    queryKey: depKey(companyId, assetId),
    queryFn: () =>
      api<DepreciationEntry[]>(`/companies/${companyId}/fixed-assets/${assetId}/depreciation`),
    enabled: !!companyId && !!assetId,
  });
}

export function useCreateFixedAsset(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateFixedAsset) =>
      apiJson<CreateFixedAsset, FixedAsset>('POST', `/companies/${companyId}/fixed-assets`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: assetsKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

export function useDisposeFixedAsset(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: DisposeFixedAsset }) =>
      apiJson<DisposeFixedAsset, FixedAsset>(
        'POST',
        `/companies/${companyId}/fixed-assets/${id}/dispose`,
        data,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: assetsKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

export function useRunDepreciation(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: RunDepreciation) =>
      apiJson<RunDepreciation, RunDepreciationResponse>(
        'POST',
        `/companies/${companyId}/fixed-assets/depreciation/run`,
        data,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: assetsKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['fixed-assets-dep', companyId] });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

export function usePendingReceipts(companyId: string | undefined) {
  return useQuery({
    queryKey: [...assetsKey(companyId), 'pending-receipts'],
    queryFn: () =>
      api<PendingReceiptRow[]>(`/companies/${companyId}/fixed-assets/receipts/pending`),
    enabled: !!companyId,
  });
}

export function useReceiveAsset(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ journalLineId, data }: { journalLineId: string; data: CapitalizeReceipt }) =>
      apiJson<CapitalizeReceipt, unknown>(
        'POST',
        `/companies/${companyId}/fixed-assets/receipts/${journalLineId}/receive`,
        data,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: assetsKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

export function useFaReconciliation(companyId: string | undefined, period?: string) {
  return useQuery({
    queryKey: [...assetsKey(companyId), 'reconciliation', period ?? null],
    queryFn: () =>
      api<FaReconciliation>(
        `/companies/${companyId}/fixed-assets/reconciliation${period ? `?period=${period}` : ''}`,
      ),
    enabled: !!companyId,
  });
}
